import { splitArray } from '@firi/shared/utils/array';
import { Button } from '@firi/ui-web';
import { Trans } from '@lingui/macro';
import { t } from '@lingui/macro';
import clsx from 'clsx';
import { Header } from 'hooks/useScrollToHeaders';
import { useState } from 'react';

import { Typography } from './Typography';

const ShowMoreButton = ({ visible, onClick }: { visible: boolean; onClick: () => void }) => (
  <Button
    size="sm"
    skin="outlined"
    onClick={onClick}
    icon={!visible ? 'chevron-down' : 'chevron-up'}
    iconPosition="right"
  >
    {!visible ? t`Show more` : t`Show less`}
  </Button>
);

const ListItem = ({
  id,
  text,
  isActive,
  className = '',
  onClick,
}: {
  id: string;
  text: string;
  isActive?: boolean;
  className?: string;
  onClick: (id: string) => void;
}) => (
  <li key={id} className={className}>
    <a
      href={`#${id}`}
      className={clsx(
        'flex cursor-pointer transition duration-300 ease-in-out align-baseline',
        isActive ? 'text-firi-blue' : 'text-black-700',
      )}
      onClick={(e) => {
        e.preventDefault();
        onClick(id);
      }}
    >
      <span
        className={clsx(
          'w-4 h-4 rounded-full flex mr-4 mt-1 flex-shrink-0 transition duration-300 ease-in-out',
          isActive ? 'bg-firi-blue' : 'bg-black-150',
        )}
      />

      <Typography
        color={isActive ? 'blue' : '2'}
        size="sm"
        className="hover:text-black-900 hover:font-semibold"
      >
        {text}
      </Typography>
    </a>
  </li>
);

export const TableOfContents = ({
  headers,
  handleScrollTo,
}: {
  headers: Header[];
  handleScrollTo: (id: string) => void;
}) => {
  const [restVisible, setRestVisible] = useState<boolean>(false);

  const handleShowItems = () => setRestVisible(!restVisible);

  const isLong = headers.length >= 7;

  const [firstItems, restItems] = splitArray(headers, 3);

  return (
    <div>
      <div className="flex justify-between items-center">
        <Typography as="h3" size="lg" color="blue">
          <Trans>Content</Trans>
        </Typography>
      </div>
      <ul className="space-y-6 mt-6">
        {firstItems.map((header) => {
          return (
            <ListItem
              key={header.key}
              id={header.key}
              text={header.title}
              onClick={handleScrollTo}
            />
          );
        })}
        {restItems.map((header) => {
          return (
            <ListItem
              className={clsx(restVisible ? 'block' : 'hidden')}
              key={header.key}
              id={header.key}
              text={header.title}
              onClick={handleScrollTo}
            />
          );
        })}
      </ul>
      {isLong && (
        <div className="flex justify-center mt-8">
          <ShowMoreButton visible={restVisible} onClick={handleShowItems} />
        </div>
      )}
    </div>
  );
};
